package ui.campaign.detailv2.editor.widget

import androidx.compose.animation.*
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.DayOfWeek
import common.frontend.api.Io.Campaign.WeatherPictograms
import compose.assets.Svg
import compose.assets.svg.*
import org.jetbrains.compose.resources.StringResource
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import tools.EFieldRegularBoolean
import tools.rememberString
import tools.toStringRes
import tools.toStringResShort
import ui.campaign.detailv2.editor.wrapper.ImplCampaignWrapperEConditionHeader
import ui.widget.*
import kotlin.math.roundToInt

@Composable
fun ImplCampaignWrapperEConditionHeader.CampaignConditionHeader(
    entryId: Any,
    actionsFooter: @Composable BoxScope.() -> Unit = {}
) {
    CampaignConditionHeaderInternal { onCollabs ->
        ItemHeaderEditor(
            entryId = entryId,
            actionsFooter = actionsFooter
        )
    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.CampaignConditionHeaderInternal(content: @Composable (onCollabs: () -> Unit) -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    val animatedRotation by animateFloatAsState(
        if (expanded) 90F
        else 0F
    )
    val display = LocalDisplayProvider.current
    Column {
        Row(
            modifier = Modifier.fillMaxWidth()
                .background(MaterialTheme.colorScheme.primaryContainer)
                .clickable {
                    expanded = !expanded
                }) {
            Icon(
                imageVector = Svg.Edit,
                contentDescription = null,
                modifier = Modifier.padding(8.dp)
            )
            FlowRow(
                Modifier.weight(1F)
                    .wrapContentHeight(Alignment.Top)
                    .align(Alignment.CenterVertically)
                    .padding(4.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                ConditionalHeaderTime()
                ConditionalHeaderDate()
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    ConditionalHeaderWeatherTemperature()
                    ConditionalHeaderWeatherHumidity()
                    ConditionalHeaderWeatherWind()
                }
                ConditionalHeaderWeatherSymbols()
            }
            if (!display.isMobileOrSmall) {
                Icon(
                    imageVector = Svg.Chevron_right,
                    contentDescription = null,
                    modifier = Modifier
                        .rotate(animatedRotation)
                        .padding(8.dp)

                )
            }
        }
        AnimatedVisibility(
            visible = expanded,
            modifier = Modifier.fillMaxWidth(),
            enter = fadeIn() + slideInVertically() + expandVertically(),
            exit = fadeOut() + slideOutVertically() + shrinkVertically()
        ) {
            Column(Modifier.fillMaxWidth()) {
                content {
                    expanded = false
                }
                HorizontalDivider()
            }
        }
    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalHeaderTime() {
    Row {
        Icon(
            imageVector = Svg.Timer_edit_outline,
            contentDescription = null,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
        Space(4.dp)
        Text(
            text = "${timeStart.state} - ${timeEnd.state}",
            style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalHeaderDate() {
    Row {
        Icon(
            imageVector = Svg.Calendar,
            contentDescription = null,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
        Space(4.dp)
        Text(
            text = rememberCampaignDaysAsString(days.state),
            style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalHeaderWeatherTemperature() {
    if (weatherTemperature.state) {
        Row {

            Icon(
                imageVector = Svg.Temperature_celsius,
                contentDescription = null,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
            Space(4.dp)
            Text(
                text = "${weatherTemperatureMin.state} .. ${weatherTemperatureMax.state}°C",
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
        }
    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalHeaderWeatherHumidity() {
    if (weatherHumidity.state) {
        Row {
            Icon(
                imageVector = Svg.Cloud_percent_outline,
                contentDescription = null,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
            Space(4.dp)
            Text(
                text = "${weatherHumidityMin.state} .. ${weatherHumidityMax.state} %",
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
        }
    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalHeaderWeatherWind() {
    if (weatherWind.state) {
        Row {
            Icon(
                imageVector = Svg.Weather_windy,
                contentDescription = null,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
            Space(4.dp)
            Text(
                text = "${weatherWindMin.state} .. ${weatherWindMax.state} m/s",
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalHeaderWeatherSymbols() {
    if (pictograms.state.isNotEmpty()) {
        FlowRow(
            modifier = Modifier.wrapContentHeight(Alignment.Top),
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            pictograms.state
                .mapNotNull { entry -> pictograms.allPictograms.firstOrNull { it.id == entry.data } }
                .forEach { entry ->
                    LazyImage(
                        category = "weather",
                        key = entry.name,
                        modifier = Modifier.size(24.dp),
                        alphaBackground = false
                    )
                }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ImplCampaignWrapperEConditionHeader.ItemHeaderEditor(
    entryId: Any,
    actionsFooter: @Composable BoxScope.() -> Unit,
) {

    FlowRow(
        Modifier.fillMaxWidth()
            .wrapContentHeight(Alignment.Top)
            .padding(16.dp),
        horizontalArrangement = Arrangement.SpaceAround,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        ConditionalTimeEditor()
        ConditionalDayEditor()
        Spacer(Modifier.fillMaxWidth())
        ConditionalWeatherTemperatureEditor()
        ConditionalWeatherHumidityEditor()
        ConditionalWeatherWindEditor()
        ConditionalWeatherSymbolsEditor(entryId)

        Box(Modifier.fillMaxWidth()) {
            actionsFooter()
        }
    }
}


@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalWeatherWindEditor() {
    var sliderPosition by remember {
        mutableStateOf(weatherWindMin.state.toFloat()..weatherWindMax.state.toFloat())
    }
    ConditionGroupWeather(
        icon = Svg.Weather_windy,
        title = Res.string.layer_top_detail__conditions_weather_wind,
        modifier = Modifier.width(384.dp),
        enabled = weatherWind,
        subtitle = {
            AnimatedVisibility(
                visible = weatherWind.state,
                modifier = Modifier.align(Alignment.CenterVertically),
                enter = fadeIn(),
                exit = fadeOut()
            ) {
                Text(text = "${sliderPosition.start.roundToInt()}  ..  ${sliderPosition.endInclusive.roundToInt()} m/s")
            }
        }
    ) {

        Column(Modifier.padding(16.dp)) {
            RangeSlider(
                value = sliderPosition,
                steps = 50,
                enabled = weatherWind.state,
                modifier = Modifier.fillMaxWidth(),
                onValueChange = { range -> sliderPosition = range },
                valueRange = 0F..50F,
                onValueChangeFinished = {
                    weatherWindMin.set(sliderPosition.start.roundToInt())
                    weatherWindMax.set(sliderPosition.endInclusive.roundToInt())
                }
            )
        }

    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalWeatherHumidityEditor() {
    var sliderPosition by remember {
        mutableStateOf(weatherHumidityMin.state.toFloat()..weatherHumidityMax.state.toFloat())
    }
    ConditionGroupWeather(
        icon = Svg.Cloud_percent_outline,
        title = Res.string.layer_top_detail__conditions_weather_humidity,
        modifier = Modifier.width(384.dp),
        enabled = weatherHumidity,
        subtitle = {
            AnimatedVisibility(
                visible = weatherHumidity.state,
                modifier = Modifier.align(Alignment.CenterVertically),
                enter = fadeIn(),
                exit = fadeOut()
            ) {
                Text(text = "${sliderPosition.start.roundToInt()}  ..  ${sliderPosition.endInclusive.roundToInt()}%")
            }
        }
    ) {

        Column(Modifier.padding(16.dp)) {
            RangeSlider(
                value = sliderPosition,
                steps = 100,
                enabled = weatherHumidity.state,
                modifier = Modifier.fillMaxWidth(),
                onValueChange = { range -> sliderPosition = range },
                valueRange = 0F..100F,
                onValueChangeFinished = {
                    weatherHumidityMin.set(sliderPosition.start.roundToInt())
                    weatherHumidityMax.set(sliderPosition.endInclusive.roundToInt())
                }
            )
        }
    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalTimeEditor() {
    val (startTimeState, endTimeState) = HourRangeState.rememberState(
        timeStart.state,
        timeEnd.state
    )
    ConditionGroup(
        icon = Svg.Timer_edit_outline,
        title = Res.string.layer_top_detail__conditions_hours_of_day,
        modifier = Modifier.width(384.dp),
        subtitle = {
            Text(
                text = "${startTimeState.rememberAsTimeStart()} - ${endTimeState.rememberAsTimeEnd()}",
                textAlign = TextAlign.End,
                modifier = Modifier.weight(1F).align(Alignment.CenterVertically)
            )
        }
    ) {
        HourRange(
            start = startTimeState,
            end = endTimeState,
            modifier = Modifier
                .fillMaxWidth()
                .padding(8.dp)
        )
    }
}


@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalDayEditor() {
    val display = LocalDisplayProvider.current
    val dayField: @Composable (DayOfWeek, Modifier) -> Unit = { day, modifier ->
        Row(
            modifier.clip(RoundedCornerShape(25))
                .clickable {
                    if (day match days.state) {
                        days.set(days.state - day.index)
                    } else {
                        days.set(days.state + day.index)
                    }
                }) {
            Checkbox(
                checked = day match days.state,
                onCheckedChange = { enabled ->
                    if (enabled) days.set(days.state + day.index)
                    else days.set(days.state - day.index)
                })
            Space(4.dp)
            Text(
                text =
                    if (display.isMobileOrSmall) DayOfWeek[day.index].toStringResShort().rememberString()
                    else DayOfWeek[day.index].toStringRes().rememberString(),
                modifier = Modifier.align(Alignment.CenterVertically)
            )
        }
    }
    ConditionGroup(
        icon = Svg.Calendar,
        title = Res.string.layer_top_detail__conditions_days_of_week,
        modifier = Modifier.width(384.dp),
    ) {
        if (display.isMobileOrSmall) {
            Column(Modifier.fillMaxWidth()) {
                DayOfWeek.all.toList()
                    .chunked(4)
                    .forEach { col ->
                        Row(Modifier.fillMaxWidth()) {
                            col.forEach { day ->
                                dayField(day, Modifier.weight(1F))
                            }
                            if (col.size == 3) Spacer(Modifier.weight(1F))
                        }
                    }
            }
        } else {
            Row(Modifier.fillMaxWidth()) {
                DayOfWeek.all.toList()
                    .chunked(5)
                    .forEach { col ->
                        Column(Modifier.weight(1F)) {
                            col.forEach { day ->
                                dayField(day, Modifier.fillMaxWidth())
                            }
                        }
                    }
            }
        }
    }
}

@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalWeatherTemperatureEditor() {
    var sliderPosition by remember {
        mutableStateOf(weatherTemperatureMin.state.toFloat()..weatherTemperatureMax.state.toFloat())
    }
    ConditionGroupWeather(
        icon = Svg.Temperature_celsius,
        title = Res.string.layer_top_detail__conditions_weather_temperature,
        modifier = Modifier.width(384.dp),
        enabled = weatherTemperature,
        subtitle = {
            AnimatedVisibility(
                visible = weatherTemperature.state,
                modifier = Modifier.align(Alignment.CenterVertically),
                enter = fadeIn(),
                exit = fadeOut()
            ) {
                Text(text = "${sliderPosition.start.roundToInt()}  ..  ${sliderPosition.endInclusive.roundToInt()}°C")
            }
        }
    ) {
        Column(Modifier.padding(16.dp)) {
            RangeSlider(
                value = sliderPosition,
                steps = 100,
                enabled = weatherTemperature.state,
                modifier = Modifier.fillMaxWidth(),
                onValueChange = { range -> sliderPosition = range },
                valueRange = -50F..50F,
                onValueChangeFinished = {
                    weatherTemperatureMin.set(sliderPosition.start.roundToInt())
                    weatherTemperatureMax.set(sliderPosition.endInclusive.roundToInt())
                }
            )
        }

    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ImplCampaignWrapperEConditionHeader.ConditionalWeatherSymbolsEditor(entryId: Any) {
    val pictogramPickerState = DialogState.remember(key = "pictograms:${entryId}")
    DialogWeatherPictogram(
        state = pictogramPickerState,
        pictograms = pictograms.allPictograms.filterNot { it.id in pictograms.state.map { item -> item.data } }
    ) { newItem ->
        if (pictograms.state.none { it.data == newItem.id }) {
            pictograms.add(newItem.id)
        }
    }
    ConditionGroup(
        icon = Svg.Weather_partly_snowy_rainy,
        title = Res.string.layer_top_detail__conditions_weather_situation,
        modifier = Modifier.width(384.dp),
        subtitle = {
            Row {
                Spacer(Modifier.weight(1F))
                SmallFloatingActionButton(
                    { pictogramPickerState.show() },
                    containerColor = MaterialTheme.colorScheme.primary
                ) {
                    Icon(Svg.Plus, null)
                }
            }
        }
    ) {
        if (pictograms.state.isNotEmpty()) {
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.padding(8.dp)
            ) {
                pictograms.state.forEach { item ->
                    LazyImage(
                        category = "weather",
                        key = item.key,
                        modifier = Modifier.size(48.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .clickable {
                                pictograms.removeAll { it.data == item.data }
                            }
                            .padding(2.dp),
                        alphaBackground = false
                    )
                }
            }
        }
    }
}


@Composable
private fun ConditionGroup(
    icon: ImageVector,
    title: StringResource,
    modifier: Modifier = Modifier,
    subtitle: @Composable RowScope.() -> Unit = {},
    body: @Composable () -> Unit
) {


    Card(modifier) {
        Column {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier
                    .padding(8.dp)
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
                Text(
                    text = title.rememberString(),
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
                subtitle()
            }
            body()
        }
    }
}


@Composable
private fun ConditionGroupWeather(
    icon: ImageVector,
    title: StringResource,
    modifier: Modifier = Modifier,
    enabled: EFieldRegularBoolean,
    subtitle: @Composable RowScope.() -> Unit,
    body: @Composable () -> Unit
) {

    Card(modifier) {
        Column {
            Row(
                Modifier
                    .clickable { enabled.set(!enabled.state) }
                    .padding(horizontal = 8.dp)) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
                Space(8.dp)
                Text(
                    text = title.rememberString(),
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
                Spacer(Modifier.weight(1F))
                subtitle()
                Space(16.dp)
                IconicSwitch(
                    checked = enabled.state,
                    onCheckedChange = {
                        enabled.set(it)
                    },
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
            body()
        }
    }
}

@Composable
private fun DialogWeatherPictogram(
    state: DialogState,
    pictograms: List<WeatherPictograms>,
    onSelect: (WeatherPictograms) -> Unit
) {
    Dialog(
        state = state,
        background = Color.Black
    ) {
        DialogUI(
            modifier = Modifier.fillMaxSize(),
            title = { Text(Res.string.layer_top_detail__conditions_weather_situation_dialog.rememberString()) },
            dismissButton = {
                TextButton({ state.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            },
            onDismissRequest = {}
        ) {
            LazyVerticalStaggeredGrid(
                columns = StaggeredGridCells.Adaptive(128.dp),
                modifier = Modifier.fillMaxSize()
            ) {
                items(pictograms.sortedBy { it.ordering }) {
                    LazyImage(
                        category = "weather",
                        key = it.name,
                        alphaBackground = false,
                        modifier = Modifier
                            .clip(RoundedCornerShape(8))
                            .clickable {
                                onSelect(it)
                                state.hide()
                            }.size(128.dp)
                            .padding(8.dp)
                    )
                }
            }
        }
    }
}